import { useAuth0 } from '@auth0/auth0-react';
import Hotjar from '@hotjar/browser';
import React, { useEffect } from 'react';
import twinlabsAiStackedLogo from '@repo/assets/logos/twinlabs-ai-stacked-logo.svg';
import { Route, Routes, useNavigate } from 'react-router-dom';

// Data
import Config from './common/Config';
import { installModelPreload } from './components/3D/installModelPreload';

// The model preloader warms the exact addresses the machines load. Module scope: config, not
// component state.
installModelPreload();

// Utils
import { clearOrganizationData } from '@repo/front-end-utils';

// Public Pages
import GetStartedPage from './pages/GetStarted/GetStartedPage';
import LoginPage from './pages/LoginPage/LoginPage';
import OnboardingPage from './pages/Onboarding/OnboardingPage';
import LinkRepairFixturePage from './pages/LinkRepairFixture/LinkRepairFixturePage';
import PlanCameraFixturePage from './pages/PlanCameraFixture/PlanCameraFixturePage';
import PlanMapLockFixturePage from './pages/PlanMapLockFixture/PlanMapLockFixturePage';
import LaunchSummaryFixturePage from './pages/LaunchSummaryFixture/LaunchSummaryFixturePage';
import PlanEntitiesFixturePage from './pages/PlanEntitiesFixture/PlanEntitiesFixturePage';
import NotFoundPage from './pages/NotFoundPage';
import PermissionDeniedPage from './pages/PermissionDeniedPage';
import SomethingWentWrongPage from './pages/SomethingWentWrongPage';
import LegalDocumentPage from './pages/LegalDocumentPage/LegalDocumentPage';

// Protected Pages
import LoginCallbackPage from './pages/Protected/LoginCallbackPage/LoginCallbackPage';

import LegalPromptPage from './pages/Protected/LegalPromptPage/LegalPromptPage';
import NotificationPage from './pages/Protected/NotificationPage/NotificationPage';

import { TwinPage } from './pages/Protected/TwinPage/TwinPage';

// Components
import { CheckmarkCircle12Filled, DismissCircle12Filled } from '@fluentui/react-icons';
import ProtectedRoute from './components/Auth0/ProtectedRoute';
import ProtectedLayout, { ProtectedRouteEntry } from './components/Auth0/ProtectedLayout';
import { forgetLegalPasses } from './components/Auth0/legalCheckMemory';
import { SinglePanelLayout } from '@repo/ui';
import { PrimaryButton } from '@repo/ui';
import { Roles } from '@repo/frontend-types';

// Context
import { NotificationContextProvider } from '@repo/front-end-state';

// CSS (Keep for now)
import './App.css';

// mapbox-gl - CommonJS module, must be required in the CRA (webpack) app context
// and passed as a prop to components that need it, so they don't depend on require() directly
// @ts-ignore - mapbox-gl doesn't have proper TypeScript exports
// eslint-disable-next-line @typescript-eslint/no-require-imports
const mapboxgl = require('mapbox-gl');

interface ProtectedPage extends ProtectedRouteEntry {
  Component: React.ComponentType<any>;
  props?: Record<string, unknown>;
}

const TL_ADMIN_ONLY = [Roles.TL_ADMIN];

/**
 * Every page under the protected layout. `App` builds both the child routes and the layout's role
 * table from this one list, so the two cannot drift.
 */
const protectedPages: ProtectedPage[] = [
  // Twin setup wizard — TL_ADMIN only (others land on /permission-denied).
  { path: '/onboarding', Component: OnboardingPage, allowedRoles: TL_ADMIN_ONLY },
  { path: '/twin', Component: TwinPage, props: { mapLib: mapboxgl } },
  /*
   * Dev-only harnesses, TL_ADMIN-gated and linked from no UI:
   * - /link-repair-fixture, the stale-register-link repair (`register-link-repair`, feature 37). The
   *   state it shows cannot be reached through the UI (the wizard loads only a twin's LATEST plan and
   *   a placed register row cannot be deleted), so this is where the before/after A/B is captured.
   * - /plan-camera-fixture, card 24's plan camera (`camera-feeds-page-plan-camera`): every marker
   *   and cone look, both control sets, on an invented plan.
   * - /plan-map-lock-fixture, the onboarding plan's locked top-down map
   *   (`onboarding-3d-plan-map-lock`): the lock, its enter and leave, on an invented plan.
   * - /launch-summary-fixture, the Go Live "view changes" list (`floor-elevation-adoption`): a floor
   *   moved up 0.2 m, diffed by the real computeModelDiff.
   * - /plan-entities-fixture, the Floor Plan design's plan entities
   *   (`floor-plan-page-plan-entities`): every space look and machine mark on an invented two-floor
   *   plan.
   *
   * ⚠ **This guard removes the ROUTES. It does not remove the MODULES**, and the difference was
   * measured: the imports above are static, this app declares no `sideEffects`, and the link-repair
   * fixture's seeded staging data shipped in the production bundle while three comments claimed it
   * could not. What keeps that data out is that each fixture's constants live inside its component,
   * which Terser drops along with the component nothing references, which is why the guard must
   * stay the literal `process.env.NODE_ENV !== 'production'` the build folds. Do NOT "fix" this by
   * moving an import into a `require` inside the guard: that keeps the module out of the graph and
   * **crashes the app at startup** — evaluating `@repo/ui/planner` during render threw "Rendered
   * fewer hooks than expected" and the login page rendered empty. Tried, measured, reverted.
   */
  ...(process.env.NODE_ENV !== 'production'
    ? [
        {
          path: '/link-repair-fixture',
          Component: LinkRepairFixturePage,
          allowedRoles: TL_ADMIN_ONLY,
        },
        {
          path: '/plan-camera-fixture',
          Component: PlanCameraFixturePage,
          allowedRoles: TL_ADMIN_ONLY,
        },
        {
          path: '/plan-map-lock-fixture',
          Component: PlanMapLockFixturePage,
          allowedRoles: TL_ADMIN_ONLY,
        },
        {
          path: '/launch-summary-fixture',
          Component: LaunchSummaryFixturePage,
          allowedRoles: TL_ADMIN_ONLY,
        },
        {
          path: '/plan-entities-fixture',
          Component: PlanEntitiesFixturePage,
          allowedRoles: TL_ADMIN_ONLY,
        },
      ]
    : []),
];

const App: React.FC = () => {
  const hotjarVersion = 6;

  useEffect(() => {
    if (Config.hotJarSiteId && window.location.hostname === 'app.twinlabs.ai') {
      Hotjar.init(parseInt(Config.hotJarSiteId), hotjarVersion);
    }
  }, []);

  const { logout } = useAuth0();
  const navigate = useNavigate();

  const handleLogout = () => {
    // Clear organization data before logout to prevent stale data from persisting
    clearOrganizationData();
    // And the terms checks this page load has passed, so the next person is asked
    forgetLegalPasses();
    logout({
      logoutParams: { returnTo: window.location.origin },
    });
  };

  return (
    <NotificationContextProvider>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LoginPage />} />
        <Route path="/callback" element={<LoginCallbackPage />} />
        {/* Public legal-document reader (spec-twin-viewer-legal-pages). Standalone — NOT inside
            SinglePanelLayout or the protected gate: both linkers open these in a new tab where the
            gate would race the auth context and redirect to login (FR7). */}
        <Route path="/legal/:slug" element={<LegalDocumentPage />} />
        <Route path="/legal/:slug/:version" element={<LegalDocumentPage />} />
        <Route element={<SinglePanelLayout centre={false} />}>
          <Route path="/get-started" element={<GetStartedPage />} />
        </Route>
        {/* The protected pages: one layout route, so moving between them never unmounts the gate
            or its providers. Each page's path, component and roles come from `protectedPages`. */}
        <Route element={<ProtectedLayout routes={protectedPages} />}>
          {protectedPages.map(({ path, Component, props }) => (
            <Route key={path} path={path} element={<Component {...props} />} />
          ))}
        </Route>

        {/* Single Panel Layouts */}
        <Route element={<SinglePanelLayout centre={true} />}>
          <Route path="/account/legal" element={<LegalPromptPage />} />
          <Route
            path="/account/log-out"
            element={
              <ProtectedRoute
                component={NotificationPage}
                componentProps={{
                  imgSrc: twinlabsAiStackedLogo,
                  imgAlt: 'Twinlabs.ai Logo',
                  header: 'Are you sure you wish<br />to log out?',
                  primaryButton: <PrimaryButton onClick={handleLogout}>Yes</PrimaryButton>,
                }}
              />
            }
          />
          <Route
            path="/account/success"
            element={
              <ProtectedRoute
                component={NotificationPage}
                componentProps={{
                  icon: <CheckmarkCircle12Filled style={{ color: '#00BBCC' }} />,
                  header: 'Success!',
                  primaryButton: (
                    <PrimaryButton
                      onClick={() => {
                        navigate('/account/dashboard');
                      }}
                    >
                      Continue
                    </PrimaryButton>
                  ),
                }}
              />
            }
          />
          <Route
            path="/account/failure"
            element={
              <ProtectedRoute
                component={NotificationPage}
                componentProps={{
                  icon: <DismissCircle12Filled style={{ color: '#FF000D' }} />,
                  header: 'Failure!',
                }}
              />
            }
          />
          <Route path="/permission-denied" element={<PermissionDeniedPage />} />
          <Route path="/something-went-wrong" element={<SomethingWentWrongPage />} />
        </Route>
        {/* Catch-All (404 Page Not Found) */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </NotificationContextProvider>
  );
};

export default App;
